import React, { useState } from 'react';
import { FileText, Check, DownloadSimple, CaretDown } from '@phosphor-icons/react';
import TiptapEditor from '../TiptapEditor';

interface MarkdownPlanEditorProps {
  markdown: string;
  onChange: (md: string) => void;
  onSave?: (md: string) => void;
  onApprove: () => void;
  isGenerating: boolean;
  currentActivity: string;
  hideToolbar?: boolean;
  hasBuiltBefore?: boolean;
  planTask?: 'create' | 'update' | null;
  saveStatus?: 'idle' | 'saving' | 'saved';
  hideApproveButton?: boolean;
  editable?: boolean;
  title?: string;
  placeholder?: string;
  downloadFilename?: string;
  updatedAt?: string | number;
  versions?: { key: string; label: string }[];
  activeVersionKey?: string;
  onSelectVersion?: (key: string) => void;
}

function formatRelativeTime(ts?: string | number | null): string {
  if (!ts) return '';
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function MarkdownPlanEditor({
  markdown, onChange, onSave, onApprove, isGenerating, currentActivity, hasBuiltBefore, planTask, saveStatus,
  hideApproveButton, hideToolbar, editable = true, title = 'Plan', placeholder, downloadFilename, updatedAt,
  versions = [], activeVersionKey, onSelectVersion,
}: MarkdownPlanEditorProps) {
  const isPlanning = !!planTask;
  const isBusy = isPlanning;
  const [versionOpen, setVersionOpen] = useState(false);
  const hasMultipleVersions = versions.length > 1;

  return (
    <div className="mde-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', minHeight: 0, position: 'relative' }}>
      {/* Toolbar — hidden when parent provides its own */}
      {!hideToolbar && <div style={{
        height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', background: 'var(--bg-surface)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileText size={14} weight="light" style={{ color: 'var(--text-secondary)' }} />
          <div
            onClick={() => hasMultipleVersions && setVersionOpen(!versionOpen)}
            style={{ cursor: hasMultipleVersions ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 400, color: 'var(--text-primary)' }}>{title}</span>
            {hasMultipleVersions && (
              <span style={{
                fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontWeight: 500,
                padding: '2px 8px', background: 'var(--bg-surface)', borderRadius: 4,
                border: '1px solid var(--border)',
              }}>
                <CaretDown size={10} weight="bold" /> {versions.find(v => v.key === activeVersionKey)?.label || 'Default'}
              </span>
            )}
          </div>
          {saveStatus === 'saving' ? (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 400 }}>Saving...</span>
          ) : saveStatus === 'saved' ? (
            <span style={{ fontSize: 'var(--fs-xs)', color: '#16a34a', fontWeight: 400 }}>Saved</span>
          ) : updatedAt ? (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 400 }}>{formatRelativeTime(updatedAt)}</span>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Download button */}
          {markdown && (downloadFilename || activeVersionKey) && (
            <button
              onClick={() => {
                // Use Arx memory key filename, fallback to downloadFilename
                const keyFilename = activeVersionKey ? activeVersionKey.split('/').pop() : null;
                const filename = keyFilename || downloadFilename || 'document.md';
                const content = title ? `# ${title}\n\n${markdown}` : markdown;
                const blob = new Blob([content], { type: 'text/markdown' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = filename;
                a.click();
                URL.revokeObjectURL(url);
              }}
              title={`Download ${activeVersionKey ? activeVersionKey.split('/').pop() : downloadFilename}`}
              style={{
                padding: '4px 10px', background: 'none', border: '1px solid var(--border)',
                borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                color: 'var(--text-muted)', transition: 'all 0.15s',
                fontSize: 'var(--fs-2xs)', fontWeight: 400, fontFamily: 'inherit',
              }}
            >
              <DownloadSimple size={13} weight="light" />
              <span>MD</span>
            </button>
          )}
        </div>
      </div>}

      {/* Version selector dropdown */}
      {versionOpen && hasMultipleVersions && (
        <div style={{
          position: 'absolute', top: 44, left: 16, zIndex: 50,
          background: 'var(--bg-primary)', border: '1px solid var(--border)',
          borderRadius: 10, minWidth: 200, maxHeight: 200, overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}>
          {versions.map(v => {
            const isActive = v.key === activeVersionKey;
            return (
              <div
                key={v.key}
                onClick={() => { onSelectVersion?.(v.key); setVersionOpen(false); }}
                style={{
                  padding: '8px 14px', cursor: 'pointer',
                  background: isActive ? 'var(--bg-surface)' : 'transparent',
                  borderRadius: 6, margin: 4,
                }}
                onMouseOver={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                onMouseOut={e => { if (!isActive) e.currentTarget.style.background = isActive ? 'var(--bg-surface)' : 'transparent'; }}
              >
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: isActive ? 500 : 400, color: 'var(--text-primary)' }}>
                  {v.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {versionOpen && <div onClick={() => setVersionOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />}

      {/* Content area — WYSIWYG editor */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <TiptapEditor
          value={markdown}
          onChange={editable ? onChange : undefined}
          placeholder={isBusy ? 'Agent is working...' : (placeholder || 'Start writing...')}
          editable={editable}
        />
      </div>

      {/* Approve & Build button removed — agent handles build automatically */}
    </div>
  );
}

